import React, { useState, useEffect } from "react";
import { apiService } from "../services/api";

// Text Input Field
export const TextField = ({ field, value, onChange, error }) => (
  <div className="form-field">
    <label>
      {field.label} {field.required && <span className="required">*</span>}
    </label>
    <textarea
      rows={2}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      disabled={field.readOnly}
      className={error ? "error" : ""}
    />
    {error && <span className="error-message">{error}</span>}
  </div>
);

// Number Input Field
export const NumberField = ({ field, value, onChange, error }) => (
  <div className="form-field">
    <label>
      {field.label} {field.required && <span className="required">*</span>}
    </label>
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      required={field.required}
      min={field.min}
      max={field.max}
      step={field.step || "any"}
      disabled={field.readOnly}
      className={error ? "error" : ""}
    />
    {error && <span className="error-message">{error}</span>}
  </div>
);

// Boolean Checkbox Field
export const BooleanField = ({ field, value, onChange, error }) => (
  <div className="form-field checkbox-field">
    <label>
      <input type="checkbox" checked={value || false} onChange={(e) => onChange(e.target.checked)} disabled={field.readOnly} />
      {field.label}
    </label>
    {error && <span className="error-message">{error}</span>}
  </div>
);

// Select/Enum Field
export const SelectField = ({ field, value, onChange, error }) => (
  <div className="form-field">
    <label>
      {field.label} {field.required && <span className="required">*</span>}
    </label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} required={field.required} disabled={field.readOnly} className={error ? "error" : ""}>
      <option value="">-- Select {field.label} --</option>
      {field.enum.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    {error && <span className="error-message">{error}</span>}
  </div>
);

// Date Field
export const DateField = ({ field, value, onChange, error }) => {
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  return (
    <div className="form-field">
      <label>
        {field.label} {field.required && <span className="required">*</span>}
      </label>
      <input
        type="date"
        value={formatDate(value)}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : null)}
        required={field.required}
        disabled={field.readOnly}
        className={error ? "error" : ""}
      />
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

// Reference Field (ObjectId) - renders as dropdown
export const ReferenceField = ({ field, value, onChange, error }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const data = await apiService.getAll(field.ref);
        setOptions(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error(`Error fetching ${field.ref}:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, [field.ref]);

  if (loading) {
    return <div className="form-field">Loading {field.label}...</div>;
  }

  return (
    <div className="form-field">
      <label>
        {field.label} {field.required && <span className="required">*</span>}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        required={field.required}
        disabled={field.readOnly}
        className={error ? "error" : ""}
      >
        <option value="">-- Select {field.label} --</option>
        {options.map((option) => (
          <option key={option._id} value={option._id}>
            {option.name || option.username || option.title || option._id}
          </option>
        ))}
      </select>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

// Array of References Field (multiple select)
export const ReferenceArrayField = ({ field, value, onChange, error }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const data = await apiService.getAll(field.ref);
        setOptions(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error(`Error fetching ${field.ref}:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, [field.ref]);

  const handleToggle = (optionId) => {
    const currentValues = value || [];
    const newValues = currentValues.includes(optionId) ? currentValues.filter((id) => id !== optionId) : [...currentValues, optionId];
    onChange(newValues);
  };

  if (loading) {
    return <div className="form-field">Loading {field.label}...</div>;
  }

  return (
    <div className="form-field">
      <label>{field.label}</label>
      <div className="checkbox-group">
        {options.map((option) => (
          <label key={option._id} className="checkbox-item">
            <input type="checkbox" checked={(value || []).includes(option._id)} onChange={() => handleToggle(option._id)} disabled={field.readOnly} />
            {option.name || option.username || option.title || option._id}
          </label>
        ))}
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

// Array of Strings Field (with predefined options)
export const StringArrayField = ({ field, value, onChange, error }) => {
  const handleToggle = (option) => {
    const currentValues = value || [];
    const newValues = currentValues.includes(option) ? currentValues.filter((v) => v !== option) : [...currentValues, option];
    onChange(newValues);
  };

  return (
    <div className="form-field">
      <label>{field.label}</label>
      <div className="checkbox-group">
        {(field.options || []).map((option) => (
          <label key={option} className="checkbox-item">
            <input type="checkbox" checked={(value || []).includes(option)} onChange={() => handleToggle(option)} disabled={field.readOnly} />
            {option}
          </label>
        ))}
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};
